import { supabase } from '../config/supabase';

export type MailRecordType = 'contact' | 'feedback' | 'newsletter' | 'user';

export type MailEventType =
  | 'contact_created'
  | 'contact_replied'
  | 'feedback_created'
  | 'newsletter_subscribed'
  | 'user_registered';

interface DispatchMailInput {
  eventType: MailEventType;
  recordType: MailRecordType;
  recordId: string;
  locale?: 'it' | 'en';
}

export class MailNotificationService {
  private static async dispatch(input: DispatchMailInput): Promise<void> {
    if (!input.recordId) return;

    try {
      const { error } = await supabase.functions.invoke('mail-dispatcher', {
        body: input,
      });

      if (error) {
        console.warn(`Mail dispatch failed for ${input.eventType}:`, error);
      }
    } catch (error) {
      console.warn(`Mail dispatch invocation error for ${input.eventType}:`, error);
    }
  }

  static notifyContactCreated(recordId: string, locale: 'it' | 'en' = 'it'): Promise<void> {
    return this.dispatch({
      eventType: 'contact_created',
      recordType: 'contact',
      recordId,
      locale,
    });
  }

  static notifyContactReplied(recordId: string, locale: 'it' | 'en' = 'it'): Promise<void> {
    return this.dispatch({
      eventType: 'contact_replied',
      recordType: 'contact',
      recordId,
      locale,
    });
  }

  static notifyFeedbackCreated(recordId: string, locale: 'it' | 'en' = 'it'): Promise<void> {
    return this.dispatch({
      eventType: 'feedback_created',
      recordType: 'feedback',
      recordId,
      locale,
    });
  }

  static notifyNewsletterSubscribed(email: string, locale: 'it' | 'en' = 'it'): Promise<void> {
    return this.dispatch({
      eventType: 'newsletter_subscribed',
      recordType: 'newsletter',
      recordId: email.trim().toLowerCase(),
      locale,
    });
  }

  /**
   * Welcome a new account.
   *
   * Only the user id travels: the dispatcher looks the address up itself, so
   * this cannot be used to mail an arbitrary person, and `email_events` has a
   * unique dedupe key, so a retry or a double submit cannot send it twice.
   */
  static notifyUserRegistered(userId: string, locale: 'it' | 'en' = 'it'): Promise<void> {
    return this.dispatch({
      eventType: 'user_registered',
      recordType: 'user',
      recordId: userId,
      locale,
    });
  }
}
